/**
 * Phase 0.5 Component G — Grant dialog.
 *
 * Pick a bundle (from the catalog), pick the scope node via the searchable
 * picker (AdminListScopeNodes by display name / target id; the raw id field
 * stays as a fallback for pasting a known id), pick an assignment type,
 * write a reason. Submit calls AdminCreateRoleAssignment.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraCombobox, SpecraText, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { PlatformUserPicker } from '../../Delivery/_shared/components/PlatformUserPicker';
import { actionLabel, ViewModeBadge } from '../Permissions/PermissionsPage';
import { useCreateDelegation, useGrantAssignment, useListBundles, useListScopeNodes } from './hooks';
// Only the types the server accepts (admin_authz.go validates primary |
// designee) — offering backup/temporary here made those grants fail with a
// confusing "unknown assignment_type". Time-boxing is the effective_end on
// any grant, not a separate type.
const ASSIGNMENT_TYPES = [
    'primary',
    'designee'
];
// Local calendar date (not UTC) — a Date-only ISO slice shifts across the
// midnight boundary in timezones behind UTC, which is exactly when "today" as
// a default matters most.
function todayDateStr() {
    const d = new Date();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}
export function GrantAssignmentDialog({ open, onOpenChange, defaultUserId = '', lockUserId = false, delegateFrom, onCreated }) {
    const [userId, setUserId] = useState(defaultUserId);
    const [bundleKey, setBundleKey] = useState('');
    const [scopeNodeId, setScopeNodeId] = useState('');
    const [scopeQuery, setScopeQuery] = useState('');
    const [assignmentType, setAssignmentType] = useState('primary');
    const [reason, setReason] = useState('');
    const [originalOwnerUserId, setOriginalOwnerUserId] = useState('');
    // Start of day, local — mirrors "Ends on"'s "runs through its last day": a
    // delegation dated today-to-Friday must cover all of today, so today's
    // start is 00:00:00, not "whenever the admin happened to click submit".
    // Defaults to today so the common "start now" case is one click.
    const [effectiveStart, setEffectiveStart] = useState(todayDateStr);
    const [effectiveEnd, setEffectiveEnd] = useState('');
    // Primary (non-designee) path only — optional, empty means starts now.
    // Kept separate from `effectiveStart` above since that state is designee-
    // only and defaults to today, not empty.
    const [primaryStart, setPrimaryStart] = useState('');
    // "Delegate…" from an assignment row (WS5): the dialog opens prefilled as a
    // designee grant of that row's role at that row's scope, owned by that
    // row's holder — the admin picks who covers and until when.
    useEffect(()=>{
        if (!open || !delegateFrom) return;
        setAssignmentType('designee');
        setBundleKey(delegateFrom.roleBundleKey);
        setScopeNodeId(delegateFrom.scopeNodeId);
        setOriginalOwnerUserId(delegateFrom.ownerUserId);
        setUserId('');
    }, [
        open,
        delegateFrom
    ]);
    const bundles = useListBundles(true);
    const scopeNodes = useListScopeNodes(scopeQuery, {
        enabled: open
    });
    const grant = useGrantAssignment();
    const createDelegation = useCreateDelegation();
    // A start not before the end — same-day is fine (start is 00:00:00, end is
    // 23:59:59 of that day), only a start dated after the end is invalid.
    const startAfterEnd = !!effectiveStart && !!effectiveEnd && effectiveStart > effectiveEnd;
    const designeeInvalid = assignmentType === 'designee' && (!originalOwnerUserId || !effectiveStart || !effectiveEnd || startAfterEnd);
    const primaryStartAfterEnd = !!primaryStart && !!effectiveEnd && primaryStart > effectiveEnd;
    const submit = async ()=>{
        if (!userId || !bundleKey || !scopeNodeId || !reason) return;
        if (designeeInvalid) return;
        if (assignmentType !== 'designee' && primaryStartAfterEnd) return;
        if (assignmentType === 'designee') {
            // The designee path now mints a delegation header + mirrored rows
            // instead of one standalone assignment — bundleKey/scopeNodeId act as
            // a narrowing filter on the owner's live grants, not a fresh grant.
            await createDelegation.mutateAsync({
                ownerUserId: originalOwnerUserId,
                delegateUserId: userId,
                // Start of day, local — see the effectiveStart comment above.
                startsAt: Timestamp.fromDate(new Date(`${effectiveStart}T00:00:00`)),
                // End of day, local — a delegation runs through its last day.
                endsAt: Timestamp.fromDate(new Date(`${effectiveEnd}T23:59:59`)),
                reason,
                roleBundleKey: bundleKey,
                scopeNodeId
            });
        } else {
            await grant.mutateAsync({
                userId,
                roleBundleKey: bundleKey,
                scopeNodeId,
                assignmentType,
                reason,
                originalOwnerUserId: originalOwnerUserId || undefined,
                // End of day, local — a delegation runs through its last day.
                effectiveEnd: effectiveEnd ? Timestamp.fromDate(new Date(`${effectiveEnd}T23:59:59`)) : undefined,
                // Optional — empty means starts now.
                effectiveStart: primaryStart ? Timestamp.fromDate(new Date(`${primaryStart}T00:00:00`)) : undefined
            });
        }
        onOpenChange(false);
        setBundleKey('');
        setScopeNodeId('');
        setReason('');
        setOriginalOwnerUserId('');
        setEffectiveStart(todayDateStr());
        setEffectiveEnd('');
        setPrimaryStart('');
        onCreated?.();
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminAccess.grantDialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Grant role bundle"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Creates a role assignment scoped to a node in the authz scope tree. Every grant writes an audit event."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4 py-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-user",
                                    children: "User"
                                }),
                                /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                    id: "grant-user",
                                    testid: Testids.adminAccess.grantUserSearch,
                                    triggerTestid: Testids.adminAccess.grantUserInput,
                                    value: userId,
                                    onChange: setUserId,
                                    disabled: lockUserId,
                                    placeholder: "Search for the user receiving the grant"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-bundle",
                                    children: "Role bundle"
                                }),
                                /*#__PURE__*/ _jsx(SpecraCombobox, {
                                    id: "grant-bundle",
                                    label: "Role bundle",
                                    options: (bundles.data ?? []).map((b)=>({
                                            value: b.key,
                                            label: `${b.displayName} (${b.key})${b.isGlobal ? ' — global' : ''}`
                                        })),
                                    value: bundleKey || undefined,
                                    onValueChange: (v)=>setBundleKey(v ?? ''),
                                    placeholder: "Select a bundle",
                                    searchPlaceholder: "Search bundles…",
                                    triggerProps: {
                                        'data-testid': Testids.adminAccess.grantBundleSelect
                                    }
                                }),
                                bundleKey && (()=>{
                                    const b = (bundles.data ?? []).find((x)=>x.key === bundleKey);
                                    if (!b) return null;
                                    return /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-1 flex flex-wrap items-center gap-1.5 rounded-md border bg-muted/20 px-2 py-1.5",
                                        children: [
                                            /*#__PURE__*/ _jsx(ViewModeBadge, {
                                                mode: b.defaultViewMode
                                            }),
                                            b.permittedActions.map((a)=>/*#__PURE__*/ _jsx("span", {
                                                    className: "rounded bg-background px-1.5 py-0.5 text-[10px] ring-1 ring-border",
                                                    children: actionLabel(a)
                                                }, a))
                                        ]
                                    });
                                })()
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-scope-search",
                                    children: "Scope"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "grant-scope-search",
                                    "data-testid": Testids.adminAccess.grantScopeSearchInput,
                                    value: scopeQuery,
                                    onChange: (e)=>setScopeQuery(e.target.value),
                                    placeholder: "Search scope nodes by name or target id"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "max-h-40 overflow-y-auto rounded-md border",
                                    children: [
                                        scopeNodes.isLoading && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "Loading scope nodes…"
                                        }),
                                        scopeNodes.data && scopeNodes.data.length === 0 && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "No matching scope nodes."
                                        }),
                                        (scopeNodes.data ?? []).map((n)=>/*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                "data-testid": Testids.adminAccess.grantScopeOption,
                                                "data-scope-node-id": n.id,
                                                onClick: ()=>setScopeNodeId(n.id),
                                                className: `block w-full px-2 py-1.5 text-left text-xs hover:bg-accent ${scopeNodeId === n.id ? 'bg-accent font-medium' : ''}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-medium",
                                                        children: n.displayName || n.targetObjectId
                                                    }),
                                                    ' ',
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            n.kind,
                                                            " · ",
                                                            n.targetObjectType,
                                                            " · ",
                                                            n.targetObjectId
                                                        ]
                                                    })
                                                ]
                                            }, n.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "grant-scope",
                                    "data-testid": Testids.adminAccess.grantScopeInput,
                                    value: scopeNodeId,
                                    onChange: (e)=>setScopeNodeId(e.target.value),
                                    placeholder: "Selected scope_node id (or paste one)"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-type",
                                    children: "Assignment type"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: assignmentType,
                                    onValueChange: setAssignmentType,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            id: "grant-type",
                                            "data-testid": Testids.adminAccess.grantTypeSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: ASSIGNMENT_TYPES.map((t)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: t,
                                                    children: t
                                                }, t))
                                        })
                                    ]
                                })
                            ]
                        }),
                        assignmentType === 'designee' ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "grant-original-owner",
                                            children: "Original owner"
                                        }),
                                        /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                            id: "grant-original-owner",
                                            testid: Testids.adminAccess.grantOriginalOwnerSearch,
                                            triggerTestid: Testids.adminAccess.grantOriginalOwnerInput,
                                            value: originalOwnerUserId,
                                            onChange: setOriginalOwnerUserId,
                                            placeholder: "Search for the user this designee is covering for"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "grant-start",
                                            children: "Starts on"
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            id: "grant-start",
                                            type: "date",
                                            "data-testid": Testids.adminAccess.grantStartDateInput,
                                            value: effectiveStart,
                                            onChange: (e)=>setEffectiveStart(e.target.value)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "grant-end",
                                            children: "Ends on"
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            id: "grant-end",
                                            type: "date",
                                            "data-testid": Testids.adminAccess.grantEndDateInput,
                                            value: effectiveEnd,
                                            onChange: (e)=>setEffectiveEnd(e.target.value)
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[11px] text-muted-foreground",
                                            children: "Required — delegation is always time-boxed (your organization caps the window; default 30 days). The delegate receives a time-boxed mirror of the access the owner holds today at this scope — not a new grant of their own."
                                        }),
                                        startAfterEnd && /*#__PURE__*/ _jsx("p", {
                                            className: "text-destructive text-xs",
                                            children: "Start date must be on or before the end date."
                                        })
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-start-primary",
                                    children: "Starts on"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "grant-start-primary",
                                    type: "date",
                                    "data-testid": Testids.adminAccess.grantStartDateInput,
                                    value: primaryStart,
                                    onChange: (e)=>setPrimaryStart(e.target.value)
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-[11px] text-muted-foreground",
                                    children: "Optional — leave blank to start immediately."
                                }),
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-end-primary",
                                    children: "Ends on"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "grant-end-primary",
                                    type: "date",
                                    "data-testid": Testids.adminAccess.grantEndDateInput,
                                    value: effectiveEnd,
                                    onChange: (e)=>setEffectiveEnd(e.target.value)
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "caption",
                                    tone: "muted",
                                    children: "Optional — the grant stops working after this day. Set one for an External Auditor’s engagement."
                                }),
                                primaryStartAfterEnd && /*#__PURE__*/ _jsx("p", {
                                    className: "text-destructive text-xs",
                                    children: "Start date must be on or before the end date."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "grant-reason",
                                    children: "Reason"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    id: "grant-reason",
                                    "data-testid": Testids.adminAccess.grantReasonInput,
                                    value: reason,
                                    onChange: (e)=>setReason(e.target.value),
                                    placeholder: "Why this grant is being created — appears in the audit log",
                                    rows: 3
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(DialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminAccess.grantSubmitButton,
                            onClick: submit,
                            disabled: grant.isPending || createDelegation.isPending || !userId || !bundleKey || !scopeNodeId || !reason || designeeInvalid || assignmentType !== 'designee' && primaryStartAfterEnd,
                            children: grant.isPending || createDelegation.isPending ? 'Granting…' : 'Grant'
                        })
                    ]
                })
            ]
        })
    });
}
